import { Client } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { initMessagesListener } from "./listeners/messagesListener";
import { initParticipantsListener } from "./listeners/participantsListener";
import { initConversationListener } from "./listeners/conversationListener";
import { ConfigState, EngagementPhase } from "../definitions";
import { initClientListeners } from "./listeners/clientListener";
import { notifications } from "../../notifications";
import { ACTION_START_SESSION, ACTION_LOAD_CONFIG } from "./actionTypes";
import { addNotification, changeEngagementPhase } from "./genericActions";
import { MESSAGES_LOAD_COUNT } from "../../constants";
import { recentChatsStorage } from "../../utils/recentChatsStorage";
import { getStoredPreEngagementData } from "../../sessionDataHandler";

export function initConfig(config: ConfigState) {
    return {
        type: ACTION_LOAD_CONFIG,
        payload: config
    };
}

export function initSession({
    token,
    conversationSid,
    name,
    query
}: {
    token: string;
    conversationSid: string;
    name: string;
    query: string;
}) {
    return async (dispatch: Dispatch) => {
        try {
            const conversationsClient = await Client.create(token);
            let conversation;
            try {
                conversation = await conversationsClient.getConversationBySid(conversationSid);
            } catch (e) {
                console.log(e);
                dispatch(addNotification(notifications.failedToInitSessionNotification("Couldn't load conversation")));
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                return;
            }

            // 拿到 conversation 立刻进聊天窗；参与者/消息后台补齐
            dispatch({
                type: ACTION_START_SESSION,
                payload: {
                    token,
                    conversationSid,
                    conversationsClient,
                    conversation,
                    users: [],
                    participants: [],
                    messages: [],
                    conversationState: conversation.state?.current,
                    currentPhase: EngagementPhase.MessagingCanvas
                }
            });

            initClientListeners(conversationsClient, dispatch);
            initConversationListener(conversation, dispatch);
            initMessagesListener(conversation, dispatch);
            initParticipantsListener(conversation, dispatch);

            // 后台补消息与参与者（不阻塞进窗）
            (async () => {
                try {
                    const [loadedParticipants, messagePage] = await Promise.all([
                        conversation.getParticipants(),
                        conversation.getMessages(MESSAGES_LOAD_COUNT)
                    ]);
                    const loadedMessages = messagePage.items;
                    const loadedUsers = await Promise.all(loadedParticipants.map(async (p) => p.getUser()));
                    dispatch({
                        type: ACTION_START_SESSION,
                        payload: {
                            token,
                            conversationSid,
                            conversationsClient,
                            conversation,
                            users: loadedUsers,
                            participants: loadedParticipants,
                            messages: loadedMessages,
                            conversationState: conversation.state?.current,
                            currentPhase: EngagementPhase.MessagingCanvas
                        }
                    });

                    try {
                        const preEngagement = getStoredPreEngagementData();
                        const email = preEngagement?.Email;
                        const lastMessage = loadedMessages?.[loadedMessages.length - 1];
                        const attributes = (conversation.attributes || {}) as Record<string, any>;
                        const formDataAttr = attributes?.pre_engagement_data?.formData || attributes?.formData || {};
                        const service = formDataAttr.service || {};
                        recentChatsStorage.upsert(email, {
                            conversationSid,
                            state: conversation.state?.current,
                            dateCreated: conversation.dateCreated?.toISOString?.() || new Date().toISOString(),
                            dateUpdated: conversation.dateUpdated?.toISOString?.() || new Date().toISOString(),
                            friendlyName: name || attributes?.pre_engagement_data?.friendlyName || "",
                            serviceType:
                                service.ServiceType || formDataAttr.ServiceType || preEngagement?.ServiceType || "",
                            preview: lastMessage?.body || query || ""
                        });
                    } catch (persistErr) {
                        log.warn("Failed to persist recent chat entry", persistErr);
                    }
                } catch (bgErr) {
                    log.warn("后台加载消息/参与者失败", bgErr);
                }
            })();
        } catch (e) {
            log.error("Something went wrong when initializing session", e);
            throw e;
        }
    };
}
